// Enhanced Editor Toolbar with color/highlight options, table support, and AI features
import { useRef, useState } from 'react';
import { useAI } from '../../contexts/AIContext.jsx';
import { summarizeNote } from '../../services/ai-config.js';
import SummaryModal from './SummaryModal.jsx';
import './Toolbar.css';

// Preset colors for text and highlight
const TEXT_COLORS = [
    { name: 'Default', value: null },
    { name: 'Red', value: '#ef4444' },
    { name: 'Orange', value: '#f97316' },
    { name: 'Yellow', value: '#eab308' },
    { name: 'Green', value: '#22c55e' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Purple', value: '#a855f7' },
    { name: 'Pink', value: '#ec4899' },
];

const HIGHLIGHT_COLORS = [
    { name: 'None', value: null },
    { name: 'Yellow', value: '#fef08a' },
    { name: 'Green', value: '#bbf7d0' },
    { name: 'Blue', value: '#bfdbfe' },
    { name: 'Purple', value: '#e9d5ff' },
    { name: 'Pink', value: '#fbcfe8' },
    { name: 'Orange', value: '#fed7aa' },
];

export default function Toolbar({ editor }) {
    const fileInputRef = useRef(null);
    const [showTableMenu, setShowTableMenu] = useState(false);
    const [showColorMenu, setShowColorMenu] = useState(false);
    const [showHighlightMenu, setShowHighlightMenu] = useState(false);

    // AI state
    const { aiEnabled, aiConfigured } = useAI();
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryResult, setSummaryResult] = useState(null);
    const [summaryError, setSummaryError] = useState(null);

    if (!editor) return null;

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result;
            editor.chain().focus().setImage({ src: base64 }).run();
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleImageUrl = () => {
        const url = prompt('Enter the image URL:');
        if (url) {
            editor.chain().focus().setImage({ src: url }).run();
        }
    };

    const insertTable = () => {
        editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
        setShowTableMenu(false);
    };

    const setTextColor = (color) => {
        if (color === null) {
            editor.chain().focus().unsetColor().run();
        } else {
            editor.chain().focus().setColor(color).run();
        }
        setShowColorMenu(false);
    };

    const setHighlight = (color) => {
        if (color === null) {
            editor.chain().focus().unsetHighlight().run();
        } else {
            editor.chain().focus().setHighlight({ color }).run();
        }
        setShowHighlightMenu(false);
    };

    // Close menus when clicking elsewhere
    const closeMenus = () => {
        setShowTableMenu(false);
        setShowColorMenu(false);
        setShowHighlightMenu(false);
    };

    // AI Summarize handler
    const handleSummarize = async () => {
        if (!editor || !aiEnabled || !aiConfigured) return;

        // Extract plain text from the editor
        const textContent = editor.getText();

        if (!textContent || textContent.trim().length < 50) {
            setSummaryError('Note is too short to summarize. Please add more content.');
            setShowSummaryModal(true);
            return;
        }

        setShowSummaryModal(true);
        setSummaryLoading(true);
        setSummaryResult(null);
        setSummaryError(null);

        try {
            const summary = await summarizeNote(textContent);
            setSummaryResult(summary);
        } catch (error) {
            setSummaryError(error.message || 'Failed to generate summary.');
        } finally {
            setSummaryLoading(false);
        }
    };

    const closeSummaryModal = () => {
        setShowSummaryModal(false);
        setSummaryResult(null);
        setSummaryError(null);
    };

    return (
        <div className="editor-toolbar" onClick={(e) => e.stopPropagation()}>
            {/* Text Formatting Group */}
            <div className="toolbar-group">
                <span className="toolbar-group-label">Format</span>
                <div className="toolbar-group-buttons">
                    <button
                        className={`toolbar-btn ${editor.isActive('bold') ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        title="Bold (Ctrl+B)"
                    >
                        <strong>B</strong>
                    </button>
                    <button
                        className={`toolbar-btn ${editor.isActive('italic') ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        title="Italic (Ctrl+I)"
                    >
                        <em>I</em>
                    </button>
                    <button
                        className={`toolbar-btn ${editor.isActive('strike') ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        title="Strikethrough"
                    >
                        <s>S</s>
                    </button>
                    <button
                        className={`toolbar-btn ${editor.isActive('code') ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleCode().run()}
                        title="Inline Code"
                    >
                        <code>&lt;/&gt;</code>
                    </button>
                </div>
            </div>

            <div className="toolbar-divider" />

            {/* Color Group */}
            <div className="toolbar-group">
                <span className="toolbar-group-label">Color</span>
                <div className="toolbar-group-buttons">
                    {/* Text Color */}
                    <div className="toolbar-dropdown">
                        <button
                            className={`toolbar-btn ${editor.isActive('textStyle') ? 'active' : ''}`}
                            onClick={() => { setShowColorMenu(!showColorMenu); setShowHighlightMenu(false); setShowTableMenu(false); }}
                            title="Text Color"
                        >
                            <span style={{ borderBottom: '3px solid var(--accent)' }}>A</span>
                        </button>
                        {showColorMenu && (
                            <div className="toolbar-dropdown-menu color-menu">
                                <div className="color-grid">
                                    {TEXT_COLORS.map((color) => (
                                        <button
                                            key={color.name}
                                            className={`color-btn ${color.value === null ? 'default' : ''}`}
                                            style={{ backgroundColor: color.value || 'var(--text-primary)' }}
                                            onClick={() => setTextColor(color.value)}
                                            title={color.name}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Highlight */}
                    <div className="toolbar-dropdown">
                        <button
                            className={`toolbar-btn ${editor.isActive('highlight') ? 'active' : ''}`}
                            onClick={() => { setShowHighlightMenu(!showHighlightMenu); setShowColorMenu(false); setShowTableMenu(false); }}
                            title="Highlight"
                        >
                            <span style={{ backgroundColor: 'var(--accent)', padding: '0 4px', borderRadius: '2px' }}>H</span>
                        </button>
                        {showHighlightMenu && (
                            <div className="toolbar-dropdown-menu color-menu">
                                <div className="color-grid">
                                    {HIGHLIGHT_COLORS.map((color) => (
                                        <button
                                            key={color.name}
                                            className={`color-btn ${color.value === null ? 'none' : ''}`}
                                            style={{ backgroundColor: color.value || 'transparent', border: color.value ? 'none' : '2px dashed var(--border)' }}
                                            onClick={() => setHighlight(color.value)}
                                            title={color.name}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="toolbar-divider" />

            {/* Headings Group */}
            <div className="toolbar-group">
                <span className="toolbar-group-label">Heading</span>
                <div className="toolbar-group-buttons">
                    <button
                        className={`toolbar-btn ${editor.isActive('heading', { level: 1 }) ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                        title="Heading 1"
                    >
                        H1
                    </button>
                    <button
                        className={`toolbar-btn ${editor.isActive('heading', { level: 2 }) ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                        title="Heading 2"
                    >
                        H2
                    </button>
                    <button
                        className={`toolbar-btn ${editor.isActive('heading', { level: 3 }) ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                        title="Heading 3"
                    >
                        H3
                    </button>
                </div>
            </div>

            <div className="toolbar-divider" />

            {/* Lists Group */}
            <div className="toolbar-group">
                <span className="toolbar-group-label">List</span>
                <div className="toolbar-group-buttons">
                    <button
                        className={`toolbar-btn ${editor.isActive('bulletList') ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        title="Bullet List"
                    >
                        •••
                    </button>
                    <button
                        className={`toolbar-btn ${editor.isActive('orderedList') ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        title="Numbered List"
                    >
                        123
                    </button>
                    <button
                        className={`toolbar-btn ${editor.isActive('blockquote') ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleBlockquote().run()}
                        title="Quote"
                    >
                        ❝
                    </button>
                </div>
            </div>

            <div className="toolbar-divider" />

            {/* Insert Group */}
            <div className="toolbar-group">
                <span className="toolbar-group-label">Insert</span>
                <div className="toolbar-group-buttons">
                    <button
                        className={`toolbar-btn ${editor.isActive('codeBlock') ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                        title="Code Block"
                    >
                        {'{ }'}
                    </button>

                    {/* Table Button with Dropdown */}
                    <div className="toolbar-dropdown">
                        <button
                            className={`toolbar-btn ${editor.isActive('table') ? 'active' : ''}`}
                            onClick={() => { setShowTableMenu(!showTableMenu); setShowColorMenu(false); setShowHighlightMenu(false); }}
                            title="Table"
                        >
                            ☰
                        </button>
                        {showTableMenu && (
                            <div className="toolbar-dropdown-menu">
                                <button onClick={insertTable}>Insert Table (3×3)</button>
                                {editor.isActive('table') && (
                                    <>
                                        <div className="dropdown-divider" />
                                        <button onClick={() => { editor.chain().focus().addColumnBefore().run(); setShowTableMenu(false); }}>
                                            Add Column Before
                                        </button>
                                        <button onClick={() => { editor.chain().focus().addColumnAfter().run(); setShowTableMenu(false); }}>
                                            Add Column After
                                        </button>
                                        <button onClick={() => { editor.chain().focus().deleteColumn().run(); setShowTableMenu(false); }}>
                                            Delete Column
                                        </button>
                                        <div className="dropdown-divider" />
                                        <button onClick={() => { editor.chain().focus().addRowBefore().run(); setShowTableMenu(false); }}>
                                            Add Row Before
                                        </button>
                                        <button onClick={() => { editor.chain().focus().addRowAfter().run(); setShowTableMenu(false); }}>
                                            Add Row After
                                        </button>
                                        <button onClick={() => { editor.chain().focus().deleteRow().run(); setShowTableMenu(false); }}>
                                            Delete Row
                                        </button>
                                        <div className="dropdown-divider" />
                                        <button onClick={() => { editor.chain().focus().deleteTable().run(); setShowTableMenu(false); }} className="danger">
                                            Delete Table
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <button
                        className="toolbar-btn"
                        onClick={() => editor.chain().focus().setHorizontalRule().run()}
                        title="Divider"
                    >
                        ―
                    </button>
                    <button
                        className="toolbar-btn"
                        onClick={() => fileInputRef.current?.click()}
                        title="Upload Image"
                    >
                        📷
                    </button>
                    <button
                        className="toolbar-btn"
                        onClick={handleImageUrl}
                        title="Image from URL"
                    >
                        🔗
                    </button>
                </div>
            </div>

            <div className="toolbar-divider" />

            {/* History Group */}
            <div className="toolbar-group">
                <span className="toolbar-group-label">History</span>
                <div className="toolbar-group-buttons">
                    <button
                        className="toolbar-btn"
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editor.can().undo()}
                        title="Undo (Ctrl+Z)"
                    >
                        ↩
                    </button>
                    <button
                        className="toolbar-btn"
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editor.can().redo()}
                        title="Redo (Ctrl+Shift+Z)"
                    >
                        ↪
                    </button>
                </div>
            </div>

            {/* AI Group - Only shown when AI is enabled */}
            {aiEnabled && aiConfigured && (
                <>
                    <div className="toolbar-divider" />
                    <div className="toolbar-group ai-group">
                        <span className="toolbar-group-label">AI</span>
                        <div className="toolbar-group-buttons">
                            <button
                                className="toolbar-btn ai-btn"
                                onClick={handleSummarize}
                                title="Summarize Note"
                            >
                                ✨
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Hidden file input for image upload */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
            />

            {/* Summary Modal */}
            <SummaryModal
                isOpen={showSummaryModal}
                onClose={closeSummaryModal}
                summary={summaryResult}
                isLoading={summaryLoading}
                error={summaryError}
            />
        </div>
    );
}
